import { CardinalOrientation, Walktour } from 'walktour'
import { useCallback, useEffect, useState } from 'react'

import { Tooltip } from 'app/KinerjaTimPenilaiPSPage/components/CoachMark/Tooltip'
import { Mask } from 'app/KinerjaTimPenilaiPSPage/components/CoachMark/Mask'

import type {
  WalktourProps,
  WalktourLogic,
  Coords,
  Dims,
  ElementInfo,
  Step,
} from 'walktour'

// the code below is a modified version of the original getTargetInfo function from walktour
export function getTargetInfo(
  root: Element,
  target?: HTMLElement
): ElementInfo | undefined {
  if (!root || !target) {
    return
  }

  const targetRect = target.getBoundingClientRect()

  const dims: Dims = {
    width: targetRect.width,
    height: targetRect.height,
  }

  const rootScrollOffset: Coords = {
    x: root.scrollLeft,
    y: root.scrollTop,
  }

  const coords: Coords = {
    x: targetRect.left + rootScrollOffset.x,
    y: targetRect.top + rootScrollOffset.y,
  }

  return {
    coords,
    dims,
  }
}

export type KinerjaCustomStep = Step & {
  /**
   * Selector for the element to be masked.
   * This may be different from the tooltip element from `selector` property.
   */
  maskSelector?: string | ((root: Element) => ElementInfo)
}

type CoachMarkProps = Omit<WalktourProps, 'steps'> & {
  /**
   * Function triggered on close event.
   * Use this callback to toggle the value of isOpen.
   * @returns void
   */
  onClose?: () => void
  /**
   * Function triggered on close event of the last step
   * @returns void
   */
  onFinish?: () => void
  onPrev?: (stepIndex: number) => void
  onNext?: (stepIndex: number) => void
  tooltipConfigs?: Record<string, any>
  resetOnClose?: boolean

  // custom step for setting maskSelector
  steps: KinerjaCustomStep[]
}

export default function KinerjaCoachMark({
  disableClose = false,
  disablePrev = false,
  isOpen: isOpenFromProp,
  onClose,
  onFinish,
  onPrev,
  onNext,
  tooltipConfigs = {},
  resetOnClose,
  ...props
}: CoachMarkProps) {
  const [isOpen, setIsOpen] = useState(isOpenFromProp)

  // Sync isOpenFromProp
  useEffect(() => {
    setIsOpen(isOpenFromProp)
  }, [isOpenFromProp])

  // Disable background scroll upon open
  useEffect(() => {
    const htmlEl = document.documentElement

    if (isOpen) {
      htmlEl.style.height = '100vh'

      htmlEl.style.width = '100vw'
      htmlEl.style.overflow = 'hidden'
      htmlEl.style.pointerEvents = 'none'
      htmlEl.style.userSelect = 'none'
    } else {
      htmlEl.style.height = ''
      htmlEl.style.width = ''
      htmlEl.style.overflow = ''
      htmlEl.style.pointerEvents = ''
      htmlEl.style.userSelect = ''
    }
  }, [isOpen])

  const renderMask = props.renderMask || Mask

  // If a step has a maskSelector, we need to get the target element
  // and pass it to the mask
  const getMaskTargetInfo = useCallback(
    (stepIndex: number, tourRoot: Element) => {
      const step = props.steps[stepIndex]

      if (!step) return undefined

      if (step.maskSelector && typeof step.maskSelector === 'function') {
        // If maskSelector is a function, call it with the tourRoot
        const targetInfo = step.maskSelector(tourRoot)
        if (targetInfo && typeof targetInfo === 'object') {
          return targetInfo as ElementInfo
        }
      }

      const target =
        step.maskSelector || step.selector
          ? document.querySelector(
              (step.maskSelector as string) ?? step.selector
            )
          : undefined

      return target ? getTargetInfo(tourRoot, target as HTMLElement) : undefined
    },
    [props.steps]
  )

  // Manually track the step index for selecting the correct mask target info.
  // Walktour's renderMask doesn't directly provide the current step index.
  const [stepIndex, setStepIndex] = useState(0)

  return (
    <Walktour
      isOpen={isOpen}
      disableClose={disableClose}
      tooltipSeparation={2}
      orientationPreferences={[
        CardinalOrientation.SOUTH,
        CardinalOrientation.WEST,
        CardinalOrientation.EAST,
        CardinalOrientation.NORTH,
      ]}
      customCloseFunc={({ stepIndex, allSteps, close }) => {
        setStepIndex(0)

        const isLastStep = stepIndex === allSteps.length - 1

        if (!disableClose || isLastStep) {
          // Invoke the onClose callback
          if (typeof onClose === 'function') onClose()

          // Invoke the onFinish callback on last step
          if (typeof onFinish === 'function' && isLastStep) onFinish()

          // Close the coachmark
          setIsOpen(false)

          // Invoke the Walktour close function to properly clear listeners
          close(resetOnClose)
        }
      }}
      customPrevFunc={({ prev, stepIndex }) => {
        setStepIndex(stepIndex - 1)

        // Invoke the onPrev callback
        if (typeof onPrev === 'function') onPrev(stepIndex)

        // Invoke the Walktour prev function to properly clear listeners
        prev()
      }}
      customNextFunc={({ next, stepIndex }) => {
        setStepIndex(stepIndex + 1)

        // Invoke the onNext callback
        if (typeof onNext === 'function') onNext(stepIndex)

        // Invoke the Walktour next function to properly clear listeners
        next()
      }}
      customTooltipRenderer={(tourLogic: WalktourLogic) => (
        <Tooltip disablePrev={disablePrev} {...tourLogic} {...tooltipConfigs} />
      )}
      renderMask={
        renderMask
          ? (props) =>
              renderMask({
                ...props,
                // set correct targetInfo for the mask
                targetInfo: getMaskTargetInfo(stepIndex, props.tourRoot),
              })
          : undefined
      }
      {...props}
    />
  )
}
